/**
 * Toast notification component using Sonner
 *
 * Configured for FlowIO styling:
 * - Position: bottom-right
 * - Stack limit: 3 visible toasts
 * - Uses primary color scheme
 */
import { Toaster as SonnerToaster } from 'sonner';

export interface ToasterProps {
  /**
   * Position of the toast notifications
   * @default 'bottom-right'
   */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';
  /**
   * Maximum number of toasts visible at once
   * @default 3
   */
  visibleToasts?: number;
}

/**
 * Toaster component - renders toast notifications
 *
 * Add this component once in the app (typically in App.tsx or root layout).
 * Then use `toast()` from 'sonner' anywhere in the app to show notifications.
 *
 * @example
 * ```tsx
 * // In App.tsx
 * import { Toaster } from '@/components/ui';
 * <Toaster />
 *
 * // Anywhere in the app
 * import { toast } from 'sonner';
 * toast.success('Document saved');
 * toast.error('Failed to save');
 * toast('Neutral message');
 * ```
 */
export default function Toaster({ position = 'bottom-right', visibleToasts = 3 }: ToasterProps) {
  return (
    <SonnerToaster
      position={position}
      visibleToasts={visibleToasts}
      toastOptions={{
        // Custom styling to match FlowIO design system
        classNames: {
          toast:
            'group bg-white border border-gray-200 shadow-lg rounded-lg p-4 flex items-start gap-3',
          title: 'text-sm font-medium text-gray-900',
          description: 'text-sm text-gray-500',
          success:
            'group-[.toast]:border-green-200 group-[.toast]:bg-green-50 [&>div>svg]:text-green-500',
          error:
            'group-[.toast]:border-red-200 group-[.toast]:bg-red-50 [&>div>svg]:text-red-500',
          warning:
            'group-[.toast]:border-yellow-200 group-[.toast]:bg-yellow-50 [&>div>svg]:text-yellow-500',
          info: 'group-[.toast]:border-primary-200 group-[.toast]:bg-primary-50 [&>div>svg]:text-primary-500',
          actionButton:
            'bg-primary-600 text-white text-sm font-medium px-3 py-1.5 rounded-md hover:bg-primary-700 transition-colors',
          cancelButton:
            'bg-gray-100 text-gray-700 text-sm font-medium px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors',
          closeButton:
            'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 rounded-md transition-colors',
        },
        // Default duration for toasts (in ms)
        duration: 4000,
      }}
      // Close button configuration
      closeButton
      // Rich colors for better visibility
      richColors
    />
  );
}

// Re-export toast function for convenience
export { toast } from 'sonner';
